import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { shareReplay, switchMap, finalize, map } from 'rxjs/operators';
import { PresignedImageApiService } from '../api/presigned-image-api.service';

interface CachedImage {
  url: string;
  expires: number;
}

@Injectable({ providedIn: 'root' })
export class PresignedUrlCacheService {
  private presignedImageApiService = inject(PresignedImageApiService);

  private cache = new Map<string, CachedImage>();
  private inFlightRequests = new Map<string, Observable<string>>();

  /**
   * Returns the cached presigned image URL if it hasn't expired,
   * otherwise fetches a new one from the backend API (reuses the request if it's already in progress).
   */
  public getImageUrl(imageId: string): Observable<string> {
    const cached = this.cache.get(imageId);
    const now = Math.floor(Date.now() / 1000);

    if (cached && cached.expires > now) {
      return of(cached.url);
    }

    const existingRequest = this.inFlightRequests.get(imageId);
    if (existingRequest) {
      return existingRequest;
    }

    const request$ = this.presignedImageApiService.getPresignedUrlImage(imageId).pipe(
      map((response) => response.url),
      switchMap((url) => {
        const expires = this.extractExpires(url);
        if (expires) {
          this.cache.set(imageId, { url, expires });
        }

        return of(url);
      }),
      shareReplay(1),
      finalize(() => this.inFlightRequests.delete(imageId)),
    );

    this.inFlightRequests.set(imageId, request$);

    return request$;
  }

  private extractExpires(url: string): number | null {
    try {
      const expires = new URL(url).searchParams.get('Expires');

      return expires ? parseInt(expires, 10) : null;
    } catch {
      return null;
    }
  }
}
